import { uniq } from 'es-toolkit';
import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Typography,
} from '@mui/material';
import { produce } from 'immer';

import { BuildingCode, DesignStandard, isTpi1VersionOrLater, LoadSettings, TrussType } from '@paragon/contract-library';
import { PropertiesPanel } from '@paragon/layout-models';
import { hasFreeSubscription } from '@paragon/login-tools';
import { assertExhaustiveSwitch, KeyToValueOfType, notNullish } from '@paragon/utilities';

import { getComponentTypeForSettingsPanel } from '../../../../../store/selectors/settingsPanelSelectors';
import { getDeterminateValueOrNull } from '../../../../dialogs/project-wide-settings/LoadedProjectWideSettingsDialog';
import { useAppSelector } from '../../../../hooks';
import { SettingsControlsProps } from '../interfaces';
import { SettingsControlsPresetToolbar } from '../preset-toolbar/SettingsControlsPresetToolbar';
import { AtticRoomLoadSettings } from './components/AtticRoomLoadSettings';
import { FloorBCLiveSettings, RoofBCLiveSettings } from './components/BCLiveSettings';
import {
  FloorBCDeadSettings,
  FloorTCDeadSettings,
  RoofBCDeadSettings,
  RoofTCDeadSettings,
} from './components/DeadSettings';
import { DragLoadSettings } from './components/DragLoadSettings';
import { DurationFactorSettings } from './components/DurationFactorSettings';
import { FloorTCLiveSettings } from './components/FloorTCLiveSettings';
import { currentDesignStandardByBuildingCode, PersonLoadSettings } from './components/PersonLoadSettings';
import { RoofTCLiveSettings } from './components/RoofTCLiveSettings';
import { SnowLoadSettings } from './components/SnowLoadSettings';
import { SprinklerLoadSettings } from './components/SprinklerLoadSettings';
import { TornadoLoadSettings } from './components/TornadoLoadSettings';
import { WindLoadSettings } from './components/WindLoadSettings';
import { DesignCode, DesignCodeField, formatBuildingCodeOrDesignStandard } from './DesignCodeField';
import { getAsce7Version, isAsce7_22OrLater } from './loadingStandards';

export interface LoadSettingsControlProps {
  readonly disabled?: boolean;
  readonly loadSettingsArray: readonly LoadSettings[];
  readonly setLoadSettingsValues: (changes: Partial<Omit<LoadSettings, 'buildingCode'>>) => void;
}

interface Option<Key = LoadSettingCategoryKey> {
  readonly key: Key;
  readonly displayName: string;
  readonly getReactComponent: () => React.ReactNode;

  /**
   * If set, this section will be a part of the basic "Roof Live + Dead" or "Floor Live + Dead" section.
   * React components in this section should use Material UI <Grid item> components.
   */
  readonly roofBasic?: boolean;
  readonly floorBasic?: boolean;

  /**
   * If set, marks this section as optional.  The given key should be for a boolean property of LoadSettings which
   *   indicates whether or not the section is enabled/considered.
   */
  readonly enabledBy?:
    | KeyToValueOfType<LoadSettings, boolean>
    | {
        readonly key: KeyToValueOfType<LoadSettings, boolean>;
        readonly patchToggledValue: (newValue: boolean) => Partial<LoadSettings>;
      };

  readonly locked?: () => boolean;
}

type LoadSettingCategoryKey =
  | 'roofTcLive'
  | 'snow'
  | 'roofBcLive'
  | 'wind'
  | 'tornado'
  | 'roofTcDead'
  | 'roofBcDead'
  | 'roofDurationFactors'
  | 'floorTcDead'
  | 'floorBcDead'
  | 'floorTcLive'
  | 'floorBcLive'
  | 'floorDurationFactors'
  | 'atticRoomSettings'
  | 'sprinklerSettings'
  | 'person'
  | 'dragLoads'
  | 'durationFactors';
type LoadSettingsCategories = {
  [K in LoadSettingCategoryKey]: Option<K>;
};

interface Props {
  readonly isViewOnly: boolean;
  readonly showHeader?: boolean /** default: true */;
  readonly showPresetControls?: boolean /** default: true */;

  readonly forceComponentType?: TrussType | 'NonComponent';

  readonly loadSettingsArray: readonly LoadSettings[];
  readonly onChange: (changes: Partial<Omit<LoadSettings, 'buildingCode'>>) => void;

  readonly designCodeElement: ReactNode;
}

export function LoadSettingsControls(props: Props) {
  const componentTypeFromSelectedModel = useAppSelector(getComponentTypeForSettingsPanel);

  const componentType = props.forceComponentType ?? componentTypeFromSelectedModel;
  if (componentType == null) {
    throw new Error('Unable to determine a selected model and `forceComponentType` not set.');
  }

  const isFreeUser = useAppSelector(hasFreeSubscription);

  const [activeSettingsTab, setActiveSettingsTab] = useState<string | null>(
    componentType === 'FloorTruss' ? 'floorBasic' : 'roofBasic',
  );

  const setLoadSettingsValues = (patch: Partial<LoadSettings>) => {
    if (props.isViewOnly) return;
    props.onChange(patch);
  };

  const doesLoadingStandardIncludeTornadoLoads = (loadSettings: LoadSettings) => {
    const loadingStandard = getAsce7Version(loadSettings.buildingCode);
    return loadingStandard != null && isAsce7_22OrLater(loadingStandard);
  };

  const doesLoadingStandardIncludePersonLoads = (loadSettings: LoadSettings) => {
    const designStandard = currentDesignStandardByBuildingCode.get(loadSettings.buildingCode);
    return designStandard != null && isTpi1VersionOrLater(designStandard, 2022);
  };

  const loadSettingsCategories: LoadSettingsCategories = {
    roofTcLive: {
      key: 'roofTcLive',
      roofBasic: true,
      displayName: 'Roof TC Live',
      getReactComponent: () => (
        <RoofTCLiveSettings
          loadSettingsArray={props.loadSettingsArray}
          disabled={props.isViewOnly}
          setLoadSettingsValues={setLoadSettingsValues}
          key="roofTcLiveSettings"
        />
      ),
    },
    snow: {
      key: 'snow',
      enabledBy: 'useSnow',
      displayName: 'Snow',
      getReactComponent: () => (
        <SnowLoadSettings
          loadSettingsArray={props.loadSettingsArray}
          disabled={props.isViewOnly || props.loadSettingsArray.every((loadSettings) => !loadSettings.useSnow)}
          setLoadSettingsValues={setLoadSettingsValues}
          key="snowSettings"
        />
      ),
    },
    roofBcLive: {
      key: 'roofBcLive',
      roofBasic: true,
      displayName: 'Roof BC Live',
      getReactComponent: () => (
        <RoofBCLiveSettings
          loadSettingsArray={props.loadSettingsArray}
          disabled={props.isViewOnly}
          setLoadSettingsValues={setLoadSettingsValues}
          key="roofBcLiveSettings"
        />
      ),
    },
    wind: {
      key: 'wind',
      enabledBy: {
        key: 'useWind',
        // If disabling wind loads, disable tornado loads too, since tornado loads use some wind load settings
        patchToggledValue: (newValue) => (newValue ? { useWind: newValue } : { useWind: false, useTornado: false }),
      },
      // If tornado loads are visible, require users to disable them manually before disabling wind loads so we don't
      // turn off tornado loads without them realizing it
      locked: () =>
        props.loadSettingsArray.some(
          (loadSettings) => doesLoadingStandardIncludeTornadoLoads(loadSettings) && loadSettings.useTornado,
        ),
      displayName: 'Wind',
      getReactComponent: () => (
        <WindLoadSettings
          loadSettingsArray={props.loadSettingsArray}
          disabled={props.isViewOnly || props.loadSettingsArray.every((loadSettings) => !loadSettings.useWind)}
          setLoadSettingsValues={setLoadSettingsValues}
          key="windSettings"
        />
      ),
    },
    tornado: {
      key: 'tornado',
      enabledBy: 'useTornado',
      // This prevents users from enabling tornado loads without enabling wind loads first, since tornado loads use some
      // wind load settings
      locked: () => !props.loadSettingsArray.every((loadSettings) => loadSettings.useWind),
      displayName: 'Tornado',
      getReactComponent: () => (
        <TornadoLoadSettings
          loadSettingsArray={props.loadSettingsArray}
          disabled={props.isViewOnly || props.loadSettingsArray.every((loadSettings) => !loadSettings.useTornado)}
          setLoadSettingsValues={setLoadSettingsValues}
          key="tornadoSettings"
        />
      ),
    },
    roofTcDead: {
      key: 'roofTcDead',
      roofBasic: true,
      displayName: 'Roof TC Dead',
      getReactComponent: () => (
        <RoofTCDeadSettings
          loadSettingsArray={props.loadSettingsArray}
          disabled={props.isViewOnly}
          setLoadSettingsValues={setLoadSettingsValues}
          key="roofTcDeadSettings"
        />
      ),
    },
    roofBcDead: {
      key: 'roofBcDead',
      roofBasic: true,
      displayName: 'Roof BC Dead',
      getReactComponent: () => (
        <RoofBCDeadSettings
          loadSettingsArray={props.loadSettingsArray}
          disabled={props.isViewOnly}
          setLoadSettingsValues={setLoadSettingsValues}
          key="roofBcDeadSettings"
        />
      ),
    },
    roofDurationFactors: {
      key: 'roofDurationFactors',
      roofBasic: true,
      displayName: 'Basic Load Duration Factors',
      getReactComponent: () => (
        <Grid size={12} key="roofDurationFactors">
          <DurationFactorSettings
            loadTypes={['dead', 'roofLive', 'floorLive']}
            loadSettingsArray={props.loadSettingsArray}
            disabled={props.isViewOnly}
            setLoadSettingsValues={setLoadSettingsValues}
          />
        </Grid>
      ),
    },
    person: {
      key: 'person',
      roofBasic: true,
      displayName: 'Person Load',
      getReactComponent: () => (
        <PersonLoadSettings
          loadSettingsArray={props.loadSettingsArray}
          disabled={
            props.isViewOnly ||
            props.loadSettingsArray.every((loadSettings) => !doesLoadingStandardIncludePersonLoads(loadSettings))
          }
          setLoadSettingsValues={setLoadSettingsValues}
          key="personLoadSettings"
        />
      ),
    },
    floorTcDead: {
      key: 'floorTcDead',
      floorBasic: true,
      displayName: 'Floor TC Dead',
      getReactComponent: () => (
        <FloorTCDeadSettings
          loadSettingsArray={props.loadSettingsArray}
          disabled={props.isViewOnly}
          setLoadSettingsValues={setLoadSettingsValues}
          key="floorTcDeadSettings"
        />
      ),
    },
    floorBcDead: {
      key: 'floorBcDead',
      floorBasic: true,
      displayName: 'Floor BC Dead',
      getReactComponent: () => (
        <FloorBCDeadSettings
          loadSettingsArray={props.loadSettingsArray}
          disabled={props.isViewOnly}
          setLoadSettingsValues={setLoadSettingsValues}
          key="floorBcDeadSettings"
        />
      ),
    },
    floorTcLive: {
      key: 'floorTcLive',
      floorBasic: true,
      displayName: 'TC Floor Live',
      getReactComponent: () => (
        <FloorTCLiveSettings
          loadSettingsArray={props.loadSettingsArray}
          disabled={props.isViewOnly}
          setLoadSettingsValues={setLoadSettingsValues}
          key="floorTcLiveSettings"
        />
      ),
    },
    floorBcLive: {
      key: 'floorBcLive',
      floorBasic: true,
      displayName: 'BC Floor Live',
      getReactComponent: () => (
        <FloorBCLiveSettings
          loadSettingsArray={props.loadSettingsArray}
          disabled={props.isViewOnly}
          setLoadSettingsValues={setLoadSettingsValues}
          key="floorBcLiveSettings"
        />
      ),
    },
    floorDurationFactors: {
      key: 'floorDurationFactors',
      floorBasic: true,
      displayName: 'Basic Load Duration Factors',
      getReactComponent: () => (
        <Grid size={12} key="floorDurationFactors">
          <DurationFactorSettings
            loadTypes={['dead', 'floorLive']}
            loadSettingsArray={props.loadSettingsArray}
            disabled={props.isViewOnly}
            setLoadSettingsValues={setLoadSettingsValues}
          />
        </Grid>
      ),
    },
    atticRoomSettings: {
      key: 'atticRoomSettings',
      enabledBy: 'considerAtticLoad',
      displayName: 'Attic Room',
      getReactComponent: () => (
        <AtticRoomLoadSettings
          loadSettingsArray={props.loadSettingsArray}
          disabled={
            props.isViewOnly || props.loadSettingsArray.every((loadSettings) => !loadSettings.considerAtticLoad)
          }
          setLoadSettingsValues={setLoadSettingsValues}
          key="atticRoomLoadSettings"
        />
      ),
    },
    sprinklerSettings: {
      key: 'sprinklerSettings',
      enabledBy: 'considerSprinklerLoad',
      displayName: 'Sprinkler Installation Load',
      getReactComponent: () => (
        <SprinklerLoadSettings
          loadSettingsArray={props.loadSettingsArray}
          disabled={
            props.isViewOnly || props.loadSettingsArray.every((loadSettings) => !loadSettings.considerSprinklerLoad)
          }
          setLoadSettingsValues={setLoadSettingsValues}
          key="sprinklerLoadSettings"
        />
      ),
    },
    dragLoads: {
      key: 'dragLoads',
      enabledBy: 'considerDragLoad',
      displayName: 'Drag Loads',
      getReactComponent: () => (
        <DragLoadSettings
          loadSettingsArray={props.loadSettingsArray}
          disabled={props.isViewOnly || props.loadSettingsArray.every((loadSettings) => !loadSettings.considerDragLoad)}
          setLoadSettingsValues={setLoadSettingsValues}
          key="dragLoadSettings"
        />
      ),
    },
    durationFactors: {
      key: 'durationFactors',
      displayName: 'Load Duration (DOL) Factors',
      getReactComponent: () => (
        <DurationFactorSettings
          hasDolHeader
          loadSettingsArray={props.loadSettingsArray}
          disabled={props.isViewOnly}
          setLoadSettingsValues={setLoadSettingsValues}
          key="durationFactorSettings"
        />
      ),
    },
  };

  const getApplicableOptionKeys = (): readonly LoadSettingCategoryKey[] => {
    switch (componentType) {
      case 'FloorTruss':
        return [
          loadSettingsCategories.floorTcLive.key,
          loadSettingsCategories.floorTcDead.key,
          loadSettingsCategories.floorBcLive.key,
          loadSettingsCategories.floorBcDead.key,
          loadSettingsCategories.floorDurationFactors.key,
          loadSettingsCategories.dragLoads.key,
          loadSettingsCategories.sprinklerSettings.key,
          loadSettingsCategories.durationFactors.key,
        ];
      case 'RoofTruss':
      case 'Wall':
        return [
          loadSettingsCategories.roofTcLive.key,
          loadSettingsCategories.roofTcDead.key,
          loadSettingsCategories.roofBcLive.key,
          loadSettingsCategories.roofBcDead.key,
          loadSettingsCategories.roofDurationFactors.key,
          ...(props.loadSettingsArray.some(doesLoadingStandardIncludePersonLoads)
            ? [loadSettingsCategories.person.key]
            : []),
          loadSettingsCategories.wind.key,
          ...(props.loadSettingsArray.some((loadSettings) => doesLoadingStandardIncludeTornadoLoads(loadSettings))
            ? [loadSettingsCategories.tornado.key]
            : []),
          loadSettingsCategories.snow.key,
          loadSettingsCategories.dragLoads.key,
          loadSettingsCategories.atticRoomSettings.key,
          loadSettingsCategories.sprinklerSettings.key,
          loadSettingsCategories.durationFactors.key,
        ];
      case 'NonComponent':
        return [
          loadSettingsCategories.roofTcLive.key,
          loadSettingsCategories.roofTcDead.key,
          loadSettingsCategories.roofBcLive.key,
          loadSettingsCategories.roofBcDead.key,
          loadSettingsCategories.roofDurationFactors.key,
          ...(props.loadSettingsArray.some(doesLoadingStandardIncludePersonLoads)
            ? [loadSettingsCategories.person.key]
            : []),
          loadSettingsCategories.floorTcLive.key,
          loadSettingsCategories.floorTcDead.key,
          loadSettingsCategories.floorBcLive.key,
          loadSettingsCategories.floorBcDead.key,
          loadSettingsCategories.floorDurationFactors.key,
          loadSettingsCategories.wind.key,
          ...(props.loadSettingsArray.some((loadSettings) => doesLoadingStandardIncludeTornadoLoads(loadSettings))
            ? [loadSettingsCategories.tornado.key]
            : []),
          loadSettingsCategories.snow.key,
          loadSettingsCategories.dragLoads.key,
          loadSettingsCategories.atticRoomSettings.key,
          loadSettingsCategories.sprinklerSettings.key,
          loadSettingsCategories.durationFactors.key,
        ];
      default:
        assertExhaustiveSwitch(componentType, 'Unrecognized component type.');
    }
  };

  const onChangeActiveTab = (newTab: string) => () => {
    setActiveSettingsTab((prevState) => (prevState === newTab ? null : newTab));
  };

  const optionKeys = getApplicableOptionKeys();
  const options = optionKeys.map((name) => loadSettingsCategories[name]).filter(notNullish);

  const roofBasicOptions: Option[] = [];
  const floorBasicOptions: Option[] = [];
  const otherOptions: Option[] = [];
  for (const option of options) {
    if (option.roofBasic) roofBasicOptions.push(option);
    else if (option.floorBasic) floorBasicOptions.push(option);
    else otherOptions.push(option);
  }

  let roofBasicOptionsComponent;
  if (roofBasicOptions.length > 0) {
    roofBasicOptionsComponent = (
      <SectionAccordion
        key="roofBasic"
        checked
        indeterminate={false}
        locked
        label="Roof Live + Dead"
        expanded={activeSettingsTab === 'roofBasic'}
        onExpansionChange={onChangeActiveTab('roofBasic')}
      >
        <Grid container spacing={1} sx={{ alignItems: 'center' }}>
          {roofBasicOptions.map((option) => option.getReactComponent())}
        </Grid>
      </SectionAccordion>
    );
  }

  let floorBasicOptionsComponent;
  if (floorBasicOptions.length > 0) {
    floorBasicOptionsComponent = (
      <SectionAccordion
        key="floorBasic"
        checked
        indeterminate={false}
        locked
        label="Floor Live + Dead"
        expanded={activeSettingsTab === 'floorBasic'}
        onExpansionChange={onChangeActiveTab('floorBasic')}
      >
        <Grid container spacing={1} sx={{ alignItems: 'center' }}>
          {floorBasicOptions.map((option) => option.getReactComponent())}
        </Grid>
      </SectionAccordion>
    );
  }

  const buildingCodes = uniq(props.loadSettingsArray.map((loadSettings) => loadSettings.buildingCode))
    .map((buildingCode) => formatBuildingCodeOrDesignStandard(buildingCode))
    .join(', ');

  const determinateSettings = getDeterminateValueOrNull(props.loadSettingsArray);

  let generalOptionsComponent: ReactNode = null;
  if (props.loadSettingsArray.length > 0 && props.designCodeElement) {
    generalOptionsComponent = (
      <SectionAccordion
        key="general"
        checked={null}
        indeterminate={false}
        label="General"
        expanded={activeSettingsTab === 'general'}
        onExpansionChange={onChangeActiveTab('general')}
      >
        {props.designCodeElement}
      </SectionAccordion>
    );
  }

  return (
    <PropertiesPanel
      title={props.showHeader !== false ? 'Load Settings' : undefined}
      subtitle={
        props.showHeader !== false && props.loadSettingsArray.length > 0 ? `Building Code: ${buildingCodes}` : undefined
      }
    >
      {props.showPresetControls !== false && (
        <>
          <SettingsControlsPresetToolbar
            settings={determinateSettings}
            settingsType="loadSettings"
            onOverwriteSettingsFromPreset={(settings) => props.onChange(settings)}
          />
          <Divider />
        </>
      )}

      <Box sx={{ m: 2 }}>
        {props.loadSettingsArray.length === 0 ? (
          <Typography sx={{ height: '100vh', textAlign: 'center' }}>
            Select object(s) on the left to view/edit settings.
          </Typography>
        ) : (
          <>
            {generalOptionsComponent}
            {roofBasicOptionsComponent}
            {floorBasicOptionsComponent}
            {otherOptions.map((option) => {
              const { enabledBy } = option;

              const sectionIsEnabledFunction = (loadSettings: LoadSettings) =>
                !enabledBy ||
                loadSettings[
                  typeof enabledBy === 'string'
                    ? enabledBy
                    : typeof enabledBy === 'object'
                      ? enabledBy.key
                      : assertExhaustiveSwitch(enabledBy)
                ];

              const selectionIsEnabledForSome = props.loadSettingsArray.some(sectionIsEnabledFunction);
              const selectionIsEnabledForEvery = props.loadSettingsArray.every(sectionIsEnabledFunction);

              return (
                <SectionAccordion
                  key={option.key}
                  label={option.displayName}
                  expanded={option.key === activeSettingsTab}
                  checked={enabledBy == null ? null : selectionIsEnabledForEvery}
                  indeterminate={selectionIsEnabledForSome && !selectionIsEnabledForEvery}
                  locked={isFreeUser || (option.locked && option.locked())}
                  onExpansionChange={onChangeActiveTab(option.key)}
                  onCheckedChange={(newValue) => {
                    switch (typeof enabledBy) {
                      case 'string':
                        setLoadSettingsValues({ [enabledBy]: newValue });
                        break;
                      case 'object':
                        setLoadSettingsValues(enabledBy.patchToggledValue(newValue));
                        break;
                      case 'undefined':
                        break;
                      default:
                        assertExhaustiveSwitch(enabledBy);
                    }
                  }}
                >
                  {option.getReactComponent()}
                </SectionAccordion>
              );
            })}
          </>
        )}
      </Box>
    </PropertiesPanel>
  );
}

interface SectionAccordionProps {
  readonly label: string;
  readonly checked: boolean | null;
  readonly indeterminate: boolean;
  readonly locked?: boolean;
  readonly expanded: boolean;
  readonly children: ReactNode;
  readonly onExpansionChange: () => void;
  readonly onCheckedChange?: (newValue: boolean) => void;
}

function SectionAccordion(props: SectionAccordionProps) {
  return (
    <Accordion expanded={props.expanded} onChange={props.onExpansionChange}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <FormControlLabel
          onFocus={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
          control={
            props.checked == null ? (
              <Box sx={{ m: 1 }} />
            ) : (
              <Checkbox
                checked={props.checked}
                indeterminate={props.indeterminate}
                onChange={(event) => {
                  if (props.locked) {
                    event.preventDefault();
                    return;
                  }

                  if (props.onCheckedChange) {
                    props.onCheckedChange(!props.checked);
                  }

                  if ((props.checked && props.expanded) || (!props.checked && !props.expanded)) {
                    props.onExpansionChange();
                  }
                }}
              />
            )
          }
          label={<Box sx={{ marginY: 1 }}>{props.label}</Box>}
        />
      </AccordionSummary>
      <AccordionDetails>{props.children}</AccordionDetails>
    </Accordion>
  );
}

// Server-side implementation in `ILoadCombination.cs`
const supportedResidentialBuildingCodes: ReadonlySet<BuildingCode> = new Set<BuildingCode>([
  BuildingCode.Crc2022,
  BuildingCode.Crc2025,
  BuildingCode.Frc2017,
  BuildingCode.Frc2020,
  BuildingCode.Frc2023,
  BuildingCode.Frc2026,
  BuildingCode.Irc2009,
  BuildingCode.Irc2012,
  BuildingCode.Irc2015,
  BuildingCode.Irc2018,
  BuildingCode.Irc2021,
  BuildingCode.Irc2024,
  BuildingCode.Rcnys2025,
]);

export function getUpdatedLoadSettingsForBuildingCode(
  loadSettings: LoadSettings,
  buildingCode: BuildingCode,
): LoadSettings {
  const oldBuildingCodeWasResidential = supportedResidentialBuildingCodes.has(loadSettings.buildingCode);
  const newBuildingCodeIsResidential = supportedResidentialBuildingCodes.has(buildingCode);

  return produce(loadSettings, (draft) => {
    draft.buildingCode = buildingCode;

    if (oldBuildingCodeWasResidential && !newBuildingCodeIsResidential) {
      draft.oneOrTwoFamilyDwelling = true;
    } else if (!oldBuildingCodeWasResidential && newBuildingCodeIsResidential) {
      if (loadSettings.oneOrTwoFamilyDwelling === false) {
        draft.useFullStorageLoad = true;
      }

      draft.oneOrTwoFamilyDwelling = null;
    }
  });
}

/**
 * The building code and design standard are presented in the same dropdown because usually the building code determines
 * the design standard. There's no clean way to present this given our current data structures, so we pass in the design
 * standard alongside the load settings for now.
 */
export interface LoadSettingsWithDesignStandard {
  readonly loadSettings: LoadSettings;
  readonly designStandard: DesignStandard;
}

/**
 * Wraps `LoadSettingsControls` for a single load settings object that integrates with the `SettingsPanelContainer`
 * system.
 */
export function SingleLoadSettingsControls({
  settingsObject,
  onChange,
  isViewOnly,
}: SettingsControlsProps<LoadSettingsWithDesignStandard>) {
  const loadSettingsArray = useMemo(() => [settingsObject.loadSettings], [settingsObject.loadSettings]);
  const onChangeLoadSettings = useCallback(
    (changes: Partial<Omit<LoadSettings, 'buildingCode'>>) =>
      onChange({ loadSettings: { ...settingsObject.loadSettings, ...changes } }),
    [onChange, settingsObject.loadSettings],
  );

  const loadSettingsWithDesignStandardsArray = useMemo(() => [settingsObject], [settingsObject]);
  const applyDesignCodeChange = useCallback(
    ([buildingCode, designStandard]: DesignCode) => {
      onChange({
        loadSettings: getUpdatedLoadSettingsForBuildingCode(settingsObject.loadSettings, buildingCode),
        designStandard,
      });
    },
    [onChange, settingsObject.loadSettings],
  );

  return (
    <LoadSettingsControls
      isViewOnly={isViewOnly}
      loadSettingsArray={loadSettingsArray}
      onChange={onChangeLoadSettings}
      designCodeElement={
        <DesignCodeField
          isViewOnly={isViewOnly}
          loadSettingsWithDesignStandardsArray={loadSettingsWithDesignStandardsArray}
          applyDesignCodeChange={applyDesignCodeChange}
        />
      }
    />
  );
}
